import React, { useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { businessAdsAPI } from '../lib/api';

export default function BusinessAdCard({ ad, variant = 'inline' }) {
  const tracked = useRef(false);

  useEffect(() => {
    if (!tracked.current && ad?.ad_id) {
      businessAdsAPI.trackImpression(ad.ad_id).catch(() => {});
      tracked.current = true;
    }
  }, [ad?.ad_id]);

  const handleClick = () => {
    if (ad?.ad_id) businessAdsAPI.trackClick(ad.ad_id).catch(() => {});
  };

  if (!ad) return null;

  const heroImg = ad.image_gallery?.[0];
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  if (variant === 'banner') {
    return (
      <div className="relative rounded-xl overflow-hidden bg-gradient-to-r from-zinc-900 to-zinc-800 group" data-testid={`business-ad-banner-${ad.ad_id}`}>
        {heroImg && (
          <img src={`${BACKEND}${heroImg}`} alt={ad.ad_title} className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity" />
        )}
        <div className="relative z-10 flex items-center gap-6 px-6 py-5 sm:px-8 sm:py-6">
          {ad.logo_image && (
            <img src={`${BACKEND}${ad.logo_image}`} alt={ad.business_name} className="w-14 h-14 rounded-xl object-cover border-2 border-white/20 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[9px] font-bold uppercase tracking-widest text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">Sponsored</span>
              {ad.category && <span className="text-[10px] text-white/50">{ad.category}</span>}
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white truncate">{ad.ad_title}</h3>
            <p className="text-sm text-white/60 line-clamp-1">{ad.ad_description || ad.business_name}</p>
          </div>
          {ad.cta_url && (
            <a href={ad.cta_url} target="_blank" rel="noopener noreferrer" onClick={handleClick}
              className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-zinc-900 text-sm font-bold hover:bg-zinc-100 transition-colors"
              data-testid={`ad-cta-${ad.ad_id}`}
            >
              {ad.cta_text || 'Learn More'} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    );
  }

  // Inline card variant (between listings)
  return (
    <div className="relative rounded-xl border border-amber-200/60 bg-gradient-to-br from-amber-50/50 to-white overflow-hidden group" data-testid={`business-ad-card-${ad.ad_id}`}>
      <div className="absolute top-3 left-3 z-10">
        <span className="text-[9px] font-bold uppercase tracking-widest text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
          Sponsored Business Advertisement
        </span>
      </div>
      {heroImg ? (
        <div className="h-40 overflow-hidden">
          <img src={`${BACKEND}${heroImg}`} alt={ad.ad_title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </div>
      ) : (
        <div className="h-40 bg-gradient-to-br from-[#002FA7]/5 to-zinc-100 flex items-center justify-center">
          {ad.logo_image ? (
            <img src={`${BACKEND}${ad.logo_image}`} alt={ad.business_name} className="w-16 h-16 rounded-xl object-cover" />
          ) : (
            <div className="text-3xl font-black text-zinc-200">{ad.business_name?.[0]}</div>
          )}
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-3 mb-2">
          {ad.logo_image && heroImg && (
            <img src={`${BACKEND}${ad.logo_image}`} alt="" className="w-8 h-8 rounded-lg object-cover border border-zinc-200 flex-shrink-0 -mt-7 relative z-10 bg-white" />
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-zinc-900 text-sm line-clamp-1">{ad.ad_title}</h4>
            <p className="text-xs text-zinc-500">{ad.business_name}{ad.city ? ` · ${ad.city}` : ''}</p>
          </div>
        </div>
        {ad.ad_description && <p className="text-xs text-zinc-600 line-clamp-2 mb-3">{ad.ad_description}</p>}
        {ad.cta_url && (
          <a href={ad.cta_url} target="_blank" rel="noopener noreferrer" onClick={handleClick}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#002FA7] text-white text-xs font-bold hover:bg-[#002585] transition-colors w-full justify-center"
            data-testid={`ad-cta-${ad.ad_id}`}
          >
            {ad.cta_text || 'Learn More'} <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}
